// Tab "Cấu hình": công tắc + 5 nhóm cấu hình (form đời thường) + JSON nâng cao.
// Lưu cấu hình: hiện Modal tóm tắt "Bạn đang đổi…" → gọi API → hiện cảnh báo của server.
import { useCallback, useEffect, useState } from "react";
import { Alert, Modal, Spin, message } from "antd";
import xuProgramApi from "../../../api/xuProgramApi";
import XuFlagsCard from "./xu-flags-card";
import XuConfigCheckinCard from "./xu-config-checkin-card";
import XuConfigMissionsCard from "./xu-config-missions-card";
import XuConfigRankCard from "./xu-config-rank-card";
import { XuConfigBudgetCard, XuConfigRulesPagesCard } from "./xu-config-safety-and-pages-cards";
import XuConfigAdvancedJson from "./xu-config-advanced-json";
import { XuConfigSideNav, XuConfigSummary } from "./xu-config-side-nav";
import XuConfigSaveBar from "./xu-config-save-bar";
import { bigIncreases, diffConfig } from "./xu-config-labels";
import { apiError } from "./xu-format-helpers";
import { resetMissionsToDefaults } from "./xu-mission-validate";

/** Khoá thuộc mỗi nhóm — nút "Khôi phục mặc định" chỉ chạm đúng các khoá này (KHÔNG đụng ngày ra mắt hạng). */
const GROUP_KEYS = {
    checkin: ["checkinRewards", "milestones", "reminder"],
    missions: ["missions", "allMissionsBonus", "missionTexts"],
    rank: ["rankTiers", "rankChannels"],
    budget: ["dailyCap", "xuTtlDays", "newUserLockDays", "budgetDaily", "budgetMonthly"],
    pages: ["rulesPages"],
};

export default function XuConfigTab({ onSettings }) {
    const [loading, setLoading] = useState(true);
    const [flags, setFlags] = useState(null);
    const [flagsDraft, setFlagsDraft] = useState(null);
    const [betaUsers, setBetaUsers] = useState([]);
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [missionMeta, setMissionMeta] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await xuProgramApi.getSettings();
            const d = data.data;
            setFlags(d.flags);
            setFlagsDraft(d.flags);
            setBetaUsers(d.betaUsers || []);
            setSaved(d.config);
            setDraft(d.config);
            setDefaults(d.defaults);
            setMissionMeta(d.missionMeta);
            onSettings?.({ flags: d.flags, rankLaunchAt: d.config.rankLaunchAt });
        } catch (e) {
            message.error(apiError(e, "Không tải được cấu hình Xu"));
        } finally {
            setLoading(false);
        }
    }, [onSettings]);
    useEffect(() => {
        load();
    }, [load]);

    if (loading || !draft || !missionMeta) return <Spin className="block mx-auto mt-10" />;

    const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
    const resetGroup = (group) => () => {
        const patch = Object.fromEntries(GROUP_KEYS[group].map((k) => [k, defaults[k]]));
        if (group === "missions") patch.missions = resetMissionsToDefaults(draft.missions, defaults.missions);
        set(patch);
    };
    const launched = Boolean(saved.rankLaunchAt) && Date.now() / 1000 >= saved.rankLaunchAt;
    const changes = diffConfig(saved, draft);

    const doSave = async () => {
        setSaving(true);
        try {
            const { data } = await xuProgramApi.saveConfig(draft);
            const { warnings: warns, ...config } = data.data;
            setSaved(config);
            setDraft(config);
            setWarnings(warns || []);
            onSettings?.({ rankLaunchAt: config.rankLaunchAt });
            message.success("Đã lưu cấu hình — áp dụng trong tối đa 30 giây");
        } catch (e) {
            message.error(apiError(e, "Lưu cấu hình thất bại"));
        } finally {
            setSaving(false);
        }
    };

    const confirmSave = () => {
        const big = bigIncreases(saved, draft);
        Modal.confirm({
            title: `Bạn đang đổi ${changes.length} mục`,
            width: 640,
            okText: "Lưu cấu hình",
            cancelText: "Xem lại",
            content: (
                <div style={{ maxHeight: 360, overflow: "auto" }}>
                    {big.length > 0 && <Alert className="mb-2" type="warning" showIcon message="Số thưởng tăng mạnh, hãy kiểm tra lại" description={big.map((b) => <div key={b}>{b}</div>)} />}
                    <ul className="list-disc pl-5">
                        {changes.map((c) => (
                            <li key={c.path}>
                                <b>{c.label}:</b> {c.from} → {c.to}
                            </li>
                        ))}
                    </ul>
                </div>
            ),
            onOk: doSave,
        });
    };

    const common = { config: draft, set };
    return (
        <div>
            <div className="flex items-start gap-5">
                <div className="flex-1 min-w-0 space-y-4">
                    <div className="xl:hidden"><XuConfigSummary flags={flags} config={draft} onReload={load} /></div>
                    <XuFlagsCard
                        saved={flags}
                        draft={flagsDraft}
                        setDraft={setFlagsDraft}
                        betaUsers={betaUsers}
                        onSaved={(f, users) => {
                            setFlags(f);
                            setFlagsDraft(f);
                            setBetaUsers(users || []);
                            onSettings?.({ flags: f });
                        }}
                    />
                    {warnings.length > 0 && (
                        <Alert type="warning" showIcon closable onClose={() => setWarnings([])} message="Đã lưu, nhưng cần lưu ý" description={warnings.map((w) => <div key={w}>{w}</div>)} />
                    )}
                    <XuConfigCheckinCard {...common} onReset={resetGroup("checkin")} />
                    <XuConfigMissionsCard {...common} missionMeta={missionMeta} onReset={resetGroup("missions")} />
                    <XuConfigRankCard {...common} onReset={resetGroup("rank")} launched={launched} defaultsTierCount={defaults.rankTiers.length} />
                    <XuConfigBudgetCard {...common} onReset={resetGroup("budget")} />
                    <XuConfigRulesPagesCard {...common} onReset={resetGroup("pages")} />
                    <XuConfigAdvancedJson config={draft} onApply={(cfg) => setDraft(cfg)} />
                </div>
                <aside className="hidden xl:block w-64 shrink-0 sticky top-4 self-start space-y-4">
                    <XuConfigSummary flags={flags} config={draft} onReload={load} />
                    <XuConfigSideNav />
                </aside>
            </div>
            <XuConfigSaveBar count={changes.length} saving={saving} onCancel={() => setDraft(saved)} onSave={confirmSave} />
        </div>
    );
}
